// Composant d'entrée texte avec masque pour les formulaires U2000, avec design system unifié
import React, { useRef } from "react";
import U2000Label from "../label/U2000Label";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import {
  formatPhoneCA,
  formatPostalCodeCA,
  getNewCursorPosition,
  isValidCharForPosition,
} from "../../../tools/U2000MaskUtils";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

interface U2000InputMaskProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  /** Libellé affiché au-dessus du champ ; optionnel (pas de label si absent). */
  label?: string;
  mask: "phone" | "postalCodeCA";
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

// Classes CSS modernes avec design system unifié (identique à U2000InputText)
const getInputClasses = (formStateHandlers: U2000FormStateHandlers<any>, fieldName: string, isFocused: boolean, readonly?: boolean) => {
  // BL-0349 : un seul fond par état (pas bg-surface + bg-muted)
  const baseClasses = "w-full px-4 py-3 text-sm border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2";
  const isDisabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName]);

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  if (isFocused) {
    return `${baseClasses} bg-surface border-blue-500 ring-blue-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  return `${baseClasses} bg-surface border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 text-gray-900 placeholder:text-gray-400 hover:border-gray-400`;
};

// Utilitaire pour accéder à une propriété imbriquée d'un objet à partir d'un chemin
function getNestedValue<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

// Composant principal avec support de masque
const U2000InputMask = <T extends Record<string, any>>({
  formStateHandlers,
  fieldName,
  label,
  mask,
  readonly = false,
  required = false,
  placeholder,
  icon,
  onFocus,
  onBlur,
  inputRef,
}: U2000InputMaskProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState<string>("");
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputElementRef = inputRef || internalInputRef;
  const previousValueRef = useRef<string>("");
  const previousSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const isReplacingTextRef = useRef<boolean>(false);

  // Fonction de formatage selon le type de masque
  const formatValue = (value: string): string => {
    if (mask === "phone") {
      return formatPhoneCA(value);
    } else if (mask === "postalCodeCA") {
      return formatPostalCodeCA(value);
    }
    return value;
  };

  // Créer un événement synthétique pour le formHandler
  const createSyntheticEvent = (
    originalEvent: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => ({
    ...originalEvent,
    target: {
      ...originalEvent.target,
      id: fieldName,
      name: fieldName,
      value,
    },
  }) as React.ChangeEvent<HTMLInputElement>;

  // Obtenir la valeur actuelle du formulaire et la formater
  const currentValue = getNestedValue(formStateHandlers, fieldName) ?? "";
  const formattedValue = currentValue ? formatValue(String(currentValue)) : "";
  
  // Synchroniser displayValue avec formattedValue quand le champ n'est pas en focus
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formattedValue);
      previousValueRef.current = formattedValue;
    }
  }, [formattedValue, isFocused]);

  // Gestion du focus
  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(true);
    setDisplayValue(formattedValue);
    previousValueRef.current = formattedValue;

    u2000SelectInputContentsOnFocus(e);
    previousSelectionRef.current = {
      start: 0,
      end: formattedValue.length,
    };
    if (onFocus) {
      onFocus(e);
    }
  }

  // Gestion du changement de valeur avec formatage en temps réel
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const inputValue = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Utiliser la valeur précédente sauvegardée ou la valeur formatée actuelle
    const oldFormattedValue = previousValueRef.current || formattedValue;
    
    // Vérifier si du texte était sélectionné avant le changement
    const hadSelection = previousSelectionRef.current && 
                         previousSelectionRef.current.end > previousSelectionRef.current.start;

    // Si du texte était sélectionné, reconstruire la valeur correctement
    let valueToFormat = inputValue;
    
    if (hadSelection && previousSelectionRef.current) {
      // Du texte était sélectionné et a été remplacé
      isReplacingTextRef.current = true;
      const digits = inputValue.replace(/\D/g, '');
      valueToFormat = digits;
    } else if (isReplacingTextRef.current) {
      // Mode remplacement actif : corriger l'ordre des caractères insérés par le navigateur
      const oldDigits = displayValue.replace(/\D/g, '');
      const newDigits = inputValue.replace(/\D/g, '');
      
      if (newDigits.length > oldDigits.length) {
        // Trouver les chiffres ajoutés en supprimant les anciens de la nouvelle chaîne
        let addedDigits = newDigits;
        for (const oldDigit of oldDigits) {
          addedDigits = addedDigits.replace(oldDigit, '');
        }
        valueToFormat = oldDigits + addedDigits;
      } else if (newDigits.length < oldDigits.length) {
        valueToFormat = newDigits;
        if (newDigits.length === 0) {
          isReplacingTextRef.current = false;
        }
      } else {
        valueToFormat = inputValue;
      }
    } else {
      valueToFormat = inputValue;
    }

    // Formater la valeur
    const newFormattedValue = formatValue(valueToFormat);

    // Calculer la nouvelle position du curseur
    let oldCursorPosForCalc = cursorPosition;
    
    if (hadSelection && previousSelectionRef.current) {
      oldCursorPosForCalc = previousSelectionRef.current.start === 0 && 
                            previousSelectionRef.current.end >= oldFormattedValue.length
        ? 0
        : previousSelectionRef.current.start;
    }

    const newCursorPosition = getNewCursorPosition(
      oldFormattedValue,
      newFormattedValue,
      oldCursorPosForCalc,
      mask
    );

    previousValueRef.current = newFormattedValue;
    setDisplayValue(newFormattedValue);
    previousSelectionRef.current = null;
    
    // Mettre à jour la valeur dans le formulaire
    if (formStateHandlers.onChange) {
      const syntheticEvent = createSyntheticEvent(e, newFormattedValue);
      formStateHandlers.onChange(syntheticEvent, newFormattedValue);
    }

    
    // Positionner le curseur
    if (isReplacingTextRef.current) {
      // En mode remplacement : forcer le curseur à la fin
      setTimeout(() => {
        if (inputElementRef.current) {
          inputElementRef.current.setSelectionRange(newFormattedValue.length, newFormattedValue.length);
        }
      }, 0);
      return;
    }

    // Mode normal : calculer la position correcte du curseur
    setTimeout(() => {
      if (inputElementRef.current) {
        inputElementRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  }

  // Gestion de la saisie au clavier pour valider les caractères
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const input = e.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const value = input.value;
    
    // Sauvegarder la sélection avant le changement (si du texte est sélectionné)
    if (selectionEnd > cursorPosition) {
      previousSelectionRef.current = {
        start: cursorPosition,
        end: selectionEnd,
      };
    }

    // Permettre les touches de navigation et de suppression
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Tab",
      "Enter",
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
        isReplacingTextRef.current = false;
      }
      return;
    }

    // Pour les autres touches, valider le caractère selon la position
    if (e.key.length === 1) {
      // Calculer la position réelle dans le masque (ignorer les caractères de formatage)
      let realPosition = 0;
      if (mask === "phone") {
        // Compter les chiffres avant le curseur
        for (let i = 0; i < cursorPosition && i < value.length; i++) {
          if (/[0-9]/.test(value[i])) {
            realPosition++;
          }
        }
      } else if (mask === "postalCodeCA") {
        // Compter les caractères alphanumériques avant le curseur
        for (let i = 0; i < cursorPosition && i < value.length; i++) {
          if (/[A-Za-z0-9]/.test(value[i])) {
            realPosition++;
          }
        }
      }

      // Valider le caractère pour cette position
      if (!isValidCharForPosition(e.key, realPosition, mask)) {
        e.preventDefault();
      }
    }
  }

  // Gestion du clic - désactiver le mode remplacement
  function handleClick() {
    isReplacingTextRef.current = false;
  }

  // Gestion du blur avec validation
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(false);
    
    // Réinitialiser la sélection et le mode remplacement au blur
    previousSelectionRef.current = null;
    isReplacingTextRef.current = false;

    // Obtenir la valeur actuelle de l'input (peut être une saisie partielle)
    const inputValue = e.target.value;
    
    // Formater la valeur finale
    const finalFormatted = formatValue(inputValue);
    
    // Mettre à jour displayValue avec la valeur formatée
    setDisplayValue(finalFormatted);
    previousValueRef.current = finalFormatted;

    // Mettre à jour la valeur dans le formulaire
    if (formStateHandlers.onChange) {
      const syntheticEvent = createSyntheticEvent(e, finalFormatted);
      formStateHandlers.onChange(syntheticEvent, finalFormatted);
    }

    if (formStateHandlers.onBlur) {
      formStateHandlers.onBlur(e);
    }

    if (onBlur) {
      onBlur(e);
    }
  }

  // Déterminer le placeholder par défaut selon le masque
  const defaultPlaceholder = placeholder || (mask === "phone" ? "(999) 999-9999" : "A1A 1A1");

  return (
    <div className="space-y-1.5">
      {label != null && label !== "" && <U2000Label label={label} required={required} htmlFor={fieldName} />}

      <div className="relative">
        {icon && <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">{icon}</div>}

        <input
          ref={inputElementRef}
          type="text"
          id={fieldName}
          name={fieldName}
          placeholder={defaultPlaceholder}
          className={`${getInputClasses(formStateHandlers, fieldName, isFocused, readonly)} ${icon ? "pl-10" : ""}`}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClick={handleClick}
          disabled={readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ}
          readOnly={readonly}
          required={required}
          aria-invalid={!!(formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName])}
          aria-describedby={formStateHandlers.getErrors()[fieldName] ? `${fieldName}-error` : undefined}
        />
      </div>

      {/* Affichage moderne du message d'erreur */}
      <U2000InvalidFeedback<T> formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputMask;
